import {
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  capitalize,
} from '@material-ui/core';

import { FilterEditorComponentProps } from '../..';
import { FilterChip } from './FilterChip';
import { FilterViewerComponentProps, createFilter } from './provider';

interface EnumFilterOptions {
  id: string;
  label: string;
  pluralLabel?: string;
  formatLabel?: (value: string) => string;
  field: string;
  enum: string[];
}

const defaultFormatLabel = (value: string) => capitalize(value.toLowerCase());

export const EnumFilterViewer = (
  props: FilterViewerComponentProps<string[]> & {
    options: string[];
    pluralLabel: string;
    formatLabel?: (value: string) => string;
  },
) => {
  const { value, formatLabel = defaultFormatLabel } = props;
  let label = '';

  if (value.length === 0) {
    label = `${props.filter.label} is empty`;
  } else if (value.length === 1) {
    if (props.filter.id === 'state' && value[0].toLowerCase() === 'active') {
      const tempLabel = 'Active';
      label = `${props.filter.label}: ${tempLabel}`;
    } else {
      label = `${props.filter.label}: ${formatLabel(value[0])}`;
    }
  } else {
    label = `${props.pluralLabel} (${value.length})`;
  }

  return <FilterChip {...props} label={label} />;
};

export const EnumFilterEditor = (
  props: FilterEditorComponentProps<string[]> & {
    options: string[];
    formatLabel?: (value: string) => string;
  },
) => {
  const {
    value,
    onValueChange,
    onClose,
    options,
    formatLabel = defaultFormatLabel,
  } = props;

  return (
    <MenuList autoFocus>
      <MenuItem
        button
        dense
        disableGutters
        onClick={() => {
          onValueChange(value?.length === options.length ? [] : options);
          onClose?.();
        }}
        style={{ paddingRight: 16 }}
      >
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={value?.length === options.length}
            onClick={e => e.stopPropagation()}
            onChange={e => onValueChange(e.target.checked ? options : [])}
            indeterminate={
              (value?.length ?? 0) > 0 &&
              (value?.length ?? 0) < (options.length ?? 0)
            }
          />
        </ListItemIcon>
        <ListItemText primary="All" />
      </MenuItem>

      <Divider />

      {options.map(item => (
        <MenuItem
          key={item}
          button
          dense
          onClick={() => {
            onValueChange(
              value?.includes(item)
                ? value?.filter(v => v !== item)
                : [...(value ?? []), item],
            );
            onClose?.();
          }}
          disableGutters
          style={{ paddingRight: 16 }}
        >
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={value?.includes(item) ?? false}
              onClick={e => e.stopPropagation()}
              onChange={e => {
                if (e.target.checked) {
                  onValueChange([...(value ?? []), item]);
                } else {
                  onValueChange(value?.filter(v => v !== item) ?? []);
                }
              }}
              tabIndex={-1}
              disableRipple
            />
          </ListItemIcon>
          <ListItemText primary={formatLabel(item)} />
        </MenuItem>
      ))}
    </MenuList>
  );
};

export const createEnumFilter = (options: EnumFilterOptions) => {
  return createFilter<string[]>({
    id: options.id,
    label: options.label,
    query: (value: string[]) => {
      return value.map(v => `${options.field}:${v}`).join(' OR ');
    },
    viewer: props => (
      <EnumFilterViewer
        options={options.enum}
        pluralLabel={options.pluralLabel ?? `${options.label}s`}
        formatLabel={options.formatLabel}
        {...props}
      />
    ),
    editor: props => (
      <EnumFilterEditor
        options={options.enum}
        formatLabel={options.formatLabel}
        {...props}
      />
    ),
  });
};
